import { useI18n } from "@/i18n/I18nContext";
import { TextPageLayout } from "@/pages/components/TextPageLayout";
import { DocumentHead } from "@/seo/DocumentHead";
import { ChevronDown, HelpCircle } from "lucide-react";

function FaqPageHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <div className="mb-12 flex flex-col items-center text-center">
      <div
        className="mb-8 flex h-[5.25rem] w-[5.25rem] items-center justify-center rounded-[1.35rem] bg-[#4CAF50] shadow-lg shadow-[#4CAF50]/25"
        aria-hidden
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white">
          <HelpCircle
            className="h-8 w-8 text-[#4CAF50]"
            strokeWidth={2}
            aria-hidden
          />
        </div>
      </div>
      <h1 className="mb-4 max-w-3xl text-4xl font-bold tracking-tight text-[#111111] lg:text-5xl lg:leading-tight">
        {title}
      </h1>
      <p className="max-w-xl text-lg text-gray-600 lg:text-xl">{subtitle}</p>
    </div>
  );
}

export function FaqPage() {
  const { messages } = useI18n();
  const f = messages.faq;

  return (
    <TextPageLayout
      pageHeader={
        <FaqPageHeader title={f.pageTitle} subtitle={f.pageSubtitle} />
      }
      containerMaxWidthClass="max-w-5xl"
    >
      <DocumentHead
        title={f.seoTitle}
        description={f.seoDescription}
        path="/faq"
      />
      <div className="flex flex-col gap-5">
        {f.items.map((faq) => (
          <details
            key={faq.question}
            className="group rounded-2xl border border-gray-200 bg-white shadow-sm open:border-gray-200 open:shadow-md"
          >
            <summary className="flex cursor-pointer list-none items-center gap-5 px-6 py-6 md:gap-6 md:px-8 md:py-8 [&::-webkit-details-marker]:hidden">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#4CAF50]/12 md:h-12 md:w-12"
                aria-hidden
              >
                <HelpCircle
                  className="h-5 w-5 text-[#4CAF50] md:h-6 md:w-6"
                  strokeWidth={2.25}
                />
              </span>
              <span className="flex-1 text-left text-lg font-semibold text-[#111111] md:text-xl">
                {faq.question}
              </span>
              <ChevronDown
                className="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 group-open:-rotate-180 md:h-6 md:w-6"
                aria-hidden
              />
            </summary>
            <div className="border-t border-gray-100 px-6 pb-6 md:px-8 md:pb-8">
              <div className="flex gap-5 pt-5 md:gap-6">
                <span className="w-11 shrink-0 md:w-12" aria-hidden />
                <p className="flex-1 text-base leading-relaxed text-gray-600 md:text-lg">
                  {faq.answer}
                </p>
              </div>
            </div>
          </details>
        ))}
      </div>

      <div className="mt-16 rounded-3xl border border-gray-200 bg-white p-8 text-center md:p-10">
        <h2 className="mb-3 text-2xl font-semibold text-[#111111] md:text-3xl">
          {f.cta.heading}
        </h2>
        <p className="mx-auto mb-6 max-w-lg text-gray-600 md:text-lg">
          {f.cta.body}
        </p>
        <a
          href="mailto:support@wasok.app"
          className="inline-block rounded-xl bg-[#4CAF50] px-8 py-3.5 font-medium text-white transition-colors duration-200 hover:bg-[#45a049]"
        >
          {f.cta.button}
        </a>
      </div>
    </TextPageLayout>
  );
}
